import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import SubtleBadge from '../../../components/common/SubtleBadge';
import formatCurrency from '../../../utils/formatCurrency';
import formatDate from '../../../utils/formatDate';
import { normalizeStoredPaymentStatus } from '../../../utils/order/orderPaymentStatus';
import { getOrderDetailPath } from '../../../utils/order/orderListPath';
import OrderListColumnFilterHeader from './OrderListColumnFilterHeader';

export const ORDER_LIST_TABLE_PAGE_SIZE = 25;

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const noteTextIncludes = (note, keyword) => {
	const q = String(keyword ?? '')
		.trim()
		.toLowerCase();
	if (!q) return false;
	return String(note ?? '')
		.toLowerCase()
		.includes(q);
};

/** 有关键词时优先展示命中备注（最多 2 条），否则回退第一条 */
const pickOrderNotesForDisplay = (orderNotes, keywords) => {
	const notes = (Array.isArray(orderNotes) ? orderNotes : []).filter((item) => String(item?.note ?? '').trim());
	if (notes.length === 0) return [];
	const queries = (Array.isArray(keywords) ? keywords : [keywords])
		.map((item) => String(item ?? '').trim())
		.filter(Boolean);
	if (queries.length === 0) return notes.slice(0, 1);
	const matched = notes.filter((item) => queries.some((query) => noteTextIncludes(item.note, query)));
	if (matched.length > 0) return matched.slice(0, 2);
	return notes.slice(0, 1);
};

const resolveNoteHighlightKeyword = (noteText, notesKeyword, listKeyword) => {
	const columnQ = String(notesKeyword ?? '').trim();
	const listQ = String(listKeyword ?? '').trim();
	if (columnQ && noteTextIncludes(noteText, columnQ)) return columnQ;
	if (listQ && noteTextIncludes(noteText, listQ)) return listQ;
	return columnQ || listQ;
};

const highlightOrderNoteText = (text, keyword) => {
	const source = String(text ?? '');
	const q = String(keyword ?? '').trim();
	if (!source || !q) return source;
	const parts = source.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
	if (parts.length <= 1) return source;
	return parts.map((part, index) => {
		if (!part) return null;
		if (part.toLowerCase() === q.toLowerCase()) {
			return (
				<mark key={`note-hk-${index}`} className='pending-keyword-mark'>
					{part}
				</mark>
			);
		}
		return <span key={`note-tx-${index}`}>{part}</span>;
	});
};

const columnFilterHeader = (label, filterKey, filterType, placeholder) =>
	({ table, column }) => (
		<OrderListColumnFilterHeader
			table={table}
			column={column}
			label={label}
			filterKey={filterKey}
			filterType={filterType}
			placeholder={placeholder}
		/>
	);


export const getOrderListPaymentStatusBadgeProps = (order) => {
	if (order?.approvalStatus === 'reject') {
		return null;
	}

	const paymentStatus = normalizeStoredPaymentStatus(order?.paymentStatus, order);
	const pendingFinalCostConfirmation = Boolean(order?.uiHints?.isPendingFinalCostConfirmation);

	switch (paymentStatus) {
		case 'waitingPayPlaceOrder':
			return { bg: 'danger', icon: 'stream', text: '待收预付款', pill: false };
		case 'waitingPayDelivery':
			return { bg: 'danger', icon: 'stream', text: '待收发货款', pill: false };
		case 'waitingPayAccept':
			return { bg: 'danger', icon: 'stream', text: '待收验收款', pill: false };
		case 'waitingPayCredit':
			return { bg: 'danger', icon: 'stream', text: '待收还款', pill: false };
		case 'payComplete':
			return {
				bg: pendingFinalCostConfirmation ? 'primary' : 'success',
				icon: 'check',
				text: pendingFinalCostConfirmation ? '待确认最终成本' : '支付完成',
				pill: true,
			};
		default:
			return null;
	}
};

const renderNotApplicableStatus = () => <span className='text-500'>-</span>;

const renderPaymentStatusBadge = (order) => {
	if (order?.approvalStatus === 'reject') {
		return renderNotApplicableStatus();
	}
	const badge = getOrderListPaymentStatusBadgeProps(order);
	if (!badge) return null;
	return (
		<SubtleBadge pill={badge.pill} bg={badge.bg} className='me-2'>
			<FontAwesomeIcon icon={badge.icon} className='me-2' />
			<span className='fs-11'>{badge.text}</span>
		</SubtleBadge>
	);
};

/** 与 manager 订单查询列表一致的列定义 */
export const buildOrderListTableColumns = () => [
	{
		id: 'order',
		header: '订单信息',
		Header: ({ column }) => <em>{column.columnDef.header}</em>,
		columns: [
			{
				accessorKey: 'orderId',
				header: columnFilterHeader('订单编号', 'orderId', 'text', '订单编号'),
				meta: {
					columnFilter: true,
					headerProps: { className: 'pe-1 text-start' },
					cellProps: { className: 'fw-semibold text-start' },
				},
				Cell: ({ row: { original } }) => {
					const { orderId, _id, approvalStatus, approval, pendingFinalCostAdjustment } = original;

					return (
						<>
							<Link to={getOrderDetailPath(_id)} className='text-decoration-none hover-primary'>
								{orderId}
							</Link>
							{approvalStatus === 'reject' && (
								<OverlayTrigger
									placement='top'
									delay={{ show: 200, hide: 100 }}
									popperConfig={{ strategy: 'fixed', modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] }}
									overlay={(props) => (
										<Tooltip {...props} id={`order-approval-rejected-${_id}`}>
											审批驳回
										</Tooltip>
									)}
								>
									<span className='ms-2 align-middle' aria-label='审批驳回'>
										<FontAwesomeIcon icon='circle-xmark' className='text-danger' style={{ fontSize: '0.875rem' }} />
									</span>
								</OverlayTrigger>
							)}
							{approvalStatus === 'inProcessOfApproval' && approval && (
								<OverlayTrigger
									overlay={
										<Tooltip style={{ position: 'fixed' }} id='approvalTooltip'>
											订单待审批，点击此处去审批
										</Tooltip>
									}
								>
									<Link to={`/approval/order/${_id}/detail/${typeof approval === 'object' ? approval._id : approval}`} className='ms-2'>
										<FontAwesomeIcon icon='circle-exclamation' className='text-danger' style={{ fontSize: '0.875rem' }} />
									</Link>
								</OverlayTrigger>
							)}
							{pendingFinalCostAdjustment && (
								<OverlayTrigger
									overlay={
										<Tooltip style={{ position: 'fixed' }} id='pendingAdjustmentTooltip'>
											有待审批的最终成本修正申请
										</Tooltip>
									}
								>
									<span className='ms-2'>
										<FontAwesomeIcon icon='exclamation-triangle' className='text-warning' style={{ fontSize: '0.875rem' }} />
									</span>
								</OverlayTrigger>
							)}
							{Boolean(original?.uiHints?.isPendingFinalCostConfirmation) && (
								<OverlayTrigger
									overlay={
										<Tooltip style={{ position: 'fixed' }} id='finalCostTooltip'>
											物流与收款已完成，待确认最终成本
										</Tooltip>
									}
								>
									<span className='ms-2'>
										<FontAwesomeIcon icon='clock' className='text-primary' style={{ fontSize: '0.875rem' }} />
									</span>
								</OverlayTrigger>
							)}
						</>
					);
				},
			},
			{
				accessorFn: (row) => row?.uiHints?.lifecycleStatusLabel ?? '未完成',
				id: 'isCompleted',
				header: '订单状态',
				meta: {
					cellProps: { className: 'fw-semibold text-start' },
				},
				Cell: ({ cell }) =>
					cell.getValue() === '已完成' ? (
						<SubtleBadge bg='success' pill>
							已完成
						</SubtleBadge>
					) : (
						<SubtleBadge bg='danger'>未完成</SubtleBadge>
					),
			},
			{
				accessorKey: 'customerInfo.customerName',
				enableClickToCopy: true,
				header: columnFilterHeader('客户名称', 'customerName', 'customer', '选择/输入客户'),
				size: 300,
				meta: {
					columnFilter: true,
					headerProps: { className: 'text-start', style: { minWidth: 160, maxWidth: 280 } },
					cellProps: { className: 'align-middle', style: { maxWidth: 280 } },
				},
				Cell: ({ cell }) => {
					const name = cell.getValue() || '';
					return (
						<span className='d-inline-block text-truncate' style={{ maxWidth: 260 }} title={name}>
							{name || '—'}
						</span>
					);
				},
			},
			{
				accessorKey: 'saleUserInfo.name',
				enableClickToCopy: true,
				header: columnFilterHeader('销售员', 'saleUserName', 'sale', '选择/输入销售员'),
				size: 100,
				meta: { columnFilter: true },
			},
			{
				accessorKey: 'assistantUserInfo.name',
				enableClickToCopy: true,
				header: columnFilterHeader('销售助理', 'assistantUserName', 'assistant', '选择/输入助理'),
				size: 100,
				meta: { columnFilter: true },
			},
			{
				accessorFn: (row) => row?.orderNotes?.[0]?.note,
				accessorKey: 'orderNotes',
				id: 'orderNotes',
				header: columnFilterHeader('备注', 'orderNotes', 'text', '备注关键词'),
				meta: {
					columnFilter: true,
					headerProps: { className: 'text-start' },
					cellProps: { className: 'align-middle', style: { maxWidth: 220 } },
				},
				Cell: ({ row: { original }, table }) => {
					const { _id, orderNotes } = original;
					const notesKeyword = table?.options?.meta?.columnFilterApplied?.orderNotes ?? '';
					const listKeyword = table?.options?.meta?.listKeyword ?? '';
					const displayNotes = pickOrderNotesForDisplay(orderNotes, [notesKeyword, listKeyword]);

					if (displayNotes.length === 0) {
						return <span className='text-500'>无备注</span>;
					}

					const tooltipText = displayNotes.map((item) => String(item.note).trim()).join(' · ');

					return (
						<OverlayTrigger
							placement='top'
							delay={{ show: 200, hide: 100 }}
							popperConfig={{
								strategy: 'fixed',
								modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
							}}
							overlay={(props) => (
								<Tooltip {...props} id={`order-note-tooltip-${_id}`}>
									{tooltipText}
								</Tooltip>
							)}
						>
							<Link to={getOrderDetailPath(_id)} className='text-decoration-none hover-primary'>
								<span className='d-inline-flex align-items-center text-truncate' style={{ maxWidth: 200 }}>
									<FontAwesomeIcon icon='fa-regular fa-comment-dots' className='text-primary me-1 flex-shrink-0' />
									<span className='text-truncate'>
										{displayNotes.map((item, index) => {
											const note = String(item.note).trim();
											const highlightQ = resolveNoteHighlightKeyword(note, notesKeyword, listKeyword);
											return (
												<span key={item._id ?? `${_id}-note-${index}`}>
													{index > 0 ? ' · ' : null}
													{highlightOrderNoteText(note, highlightQ)}
												</span>
											);
										})}
									</span>
								</span>
							</Link>
						</OverlayTrigger>
					);
				},
			},
			{
				accessorFn: (row) => new Date(row.createdAt),
				id: 'createdAt',
				header: '下单日期',
				sortingFn: 'datetime',
				Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span>,
			},
			{
				accessorFn: (row) => (row.completedAt ? new Date(row.completedAt) : null),
				id: 'completedAt',
				header: '完成日期',
				sortingFn: 'datetime',
				Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span>,
			},
			{
				accessorKey: 'paymentMethod',
				header: '支付方式',
				Cell: ({ cell }) => <span>{cell.getValue()}</span>,
			},
		],
	},
	{
		id: 'orderProcess',
		header: '订单跟进',
		Header: ({ column }) => <em>{column.columnDef.header}</em>,
		meta: {
			headerProps: { className: 'order-list-col-group-divider' },
		},
		columns: [
			{
				accessorKey: 'currentAccountsReceivable',
				header: '应收账款',
				meta: {
					headerProps: { className: 'order-list-col-group-divider' },
				},
				Cell: ({ cell, row: { original } }) => {
					if (original?.approvalStatus === 'reject') {
						return renderNotApplicableStatus();
					}
					return Number(cell.getValue()) === 0 ? (
						<SubtleBadge pill bg='success' className='me-2'>
							无应收
						</SubtleBadge>
					) : (
						<SubtleBadge bg='danger'>
							<strong>{formatCurrency(cell.getValue())}</strong>
						</SubtleBadge>
					);
				},
			},
			{
				accessorKey: 'paymentStatus',
				header: columnFilterHeader('财务状态', 'paymentStatus', 'paymentStatus', '选择/输入财务状态'),
				meta: {
					columnFilter: true,
					headerProps: { className: 'text-start' },
				},
				Cell: ({ row: { original } }) => renderPaymentStatusBadge(original),
			},
			{
				accessorKey: 'deliveryStatus',
				header: columnFilterHeader('物流状态', 'deliveryStatus', 'deliveryStatus', '选择/输入物流状态'),
				meta: {
					columnFilter: true,
					headerProps: { className: 'text-start' },
				},
				Cell: ({ row: { original } }) => {
					if (original?.approvalStatus === 'reject') {
						return renderNotApplicableStatus();
					}
					const badge = original?.uiHints?.deliveryStatusBadge;
					if (!original.deliveryStatus || !badge) {
						return <span>-</span>;
					}
					const { bg, icon, text, pill } = badge;
					return (
						<SubtleBadge pill={pill} bg={bg} className='me-2'>
							<FontAwesomeIcon icon={icon} className='me-2' />
							<span className='fs-11'>{text}</span>
						</SubtleBadge>
					);
				},
			},
		],
	},
];
